/**
 * PRODUCT_DEV-1121 ログインID を入れたあと、パスキーをどう扱うか。
 *
 * - `auto`: パスキーの画面を自動で起動する（サーバーが「あり」と返し、このブラウザに印もある）
 * - `none`: 何も出さない。ID・パスワードでログインし、ログイン後の案内でパスキーを聞く
 *
 * 印が無いブラウザ（シークレットウインドウ・別のプロファイル・別の PC）では、パスキーが使えるかを
 * 画面を出さずに確かめる方法が無い。押すと QR コードの画面になることがあるので、「パスキーでログイン」の
 * ボタンも出さない（2026-10-14 日暮さん決定）。
 */
export type PasskeyLoginMode = "auto" | "none";

export function passkeyLoginMode(args: {
  /** login-options / resolve-alias の passkeyEnabled */
  serverAvailable: boolean;
  /** このブラウザでパスキーを登録した・パスキーでログインできた印があるか */
  markedOnThisBrowser: boolean;
}): PasskeyLoginMode {
  return args.serverAvailable && args.markedOnThisBrowser ? "auto" : "none";
}

/**
 * PRODUCT_DEV-1121 パスキーの有無の問い合わせ（login-options / resolve-alias）の返事が届いたとき、
 * その結果を画面に当ててよいか。
 *
 * 問い合わせは起動直後だと数秒かかる。待つ間に利用者がログインボタンを押していると、
 * 二段階認証の途中などでパスキーの画面が開いてしまう。
 *
 * - `apply`: そのまま当てる（auto なら自動で起動する）
 * - `skip`: ログインの処理中・ログインを試した後・ログイン済み。何もしない
 */
export type PasskeyCheckOutcome = "apply" | "skip";

export function passkeyCheckOutcome(args: {
  /** ユーザ情報を取得済みか（ログイン済み） */
  signedIn: boolean;
  /** いまサインインの処理中か */
  signInInFlight: boolean;
  /** 問い合わせを始めてからサインインを試したか */
  signInTriedSinceCheck: boolean;
}): PasskeyCheckOutcome {
  return args.signedIn || args.signInInFlight || args.signInTriedSinceCheck ? "skip" : "apply";
}

/**
 * PRODUCT_DEV-1121 この版を配った日。これより前に案内を見た（または一度も見ていない）人で、
 * サーバーにパスキーがあるのにこのブラウザに印が無い人には、30 日を待たずに1回だけ案内を出す。
 * 案内を出せば `lastPasskeyPromptedAt` がこの日より後になるので、以後は 30 日に1回に戻る
 * （キャッシュが毎回消える端末で毎回出さないため）。
 */
export const PASSKEY_MARK_RELEASED_AT = "2026-10-15T00:00:00+09:00";

/**
 * PRODUCT_DEV-396 / 1121 ログイン後にパスキーの登録を勧めるか（表示の前に、端末に指紋・顔・
 * Windows Hello などがあるかを呼び出し側で確かめる）。
 *
 * - テナントがパスキーを許可していない → 出さない
 * - このブラウザに印がある → 出さない（サーバーの「この端末に登録あり」はブラウザと OS の種類でしか
 *   見分けないので使わない。シークレットウインドウでも「あり」になる）
 * - 前回の案内から 30 日（初回は出す）
 * - ただし、サーバーにパスキーがあって印が無い人は、新しい版で初めてのときだけ 30 日を待たない
 */
export function shouldPromptPasskeySetup(args: {
  tenantAllowsPasskey: boolean;
  markedOnThisBrowser: boolean;
  lastPasskeyPromptedAt: string | undefined;
  /** Cognito にこの人のパスキーが1つ以上あるか。分からなければ undefined */
  hasPasskeyOnServer: boolean | undefined;
  now?: number;
}): boolean {
  if (!args.tenantAllowsPasskey) return false;
  if (args.markedOnThisBrowser) return false;

  const promptedAt = args.lastPasskeyPromptedAt
    ? new Date(args.lastPasskeyPromptedAt).getTime()
    : NaN;
  if (!Number.isFinite(promptedAt)) return true;

  if (args.hasPasskeyOnServer && promptedAt < new Date(PASSKEY_MARK_RELEASED_AT).getTime()) {
    return true;
  }
  const daysSince = ((args.now ?? Date.now()) - promptedAt) / 86400000;
  return daysSince >= 30;
}

/**
 * PRODUCT_DEV-1121 サーバーにパスキーがあるかを聞く必要があるか（Cognito への問い合わせを1回減らす）。
 * 30 日の決まりだけで答えが出るとき・印があるときは聞かない。
 */
export function needsServerPasskeyCheck(args: {
  tenantAllowsPasskey: boolean;
  markedOnThisBrowser: boolean;
  lastPasskeyPromptedAt: string | undefined;
  now?: number;
}): boolean {
  if (shouldPromptPasskeySetup({ ...args, hasPasskeyOnServer: false })) return false;
  return shouldPromptPasskeySetup({ ...args, hasPasskeyOnServer: true });
}

/**
 * PRODUCT_DEV-1121 パスキーの画面を利用者が閉じた（キャンセルした）ときのエラーか。
 *
 * ブラウザは `NotAllowedError` を投げるが、Amplify の signIn はそれを `PasskeyAuthenticationCanceled`
 * に置き換えて投げ直す。`NotAllowedError` しか見ていなかったため、キャンセルを「パスキーが登録されていない」と
 * 扱い、印を消して「パスキーが登録されていません」と出していた（10/15 dev 確認で日暮さんが発見）。
 * キャンセルなら印は残し、何も出さずに ID・パスワードへ戻す。
 */
export function isPasskeyCancel(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return (
    name === "NotAllowedError" ||
    name === "PasskeyAuthenticationCanceled" ||
    name === "PasskeyOperationAborted"
  );
}
